import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Fade,
  useTheme
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

const ConditionalQuestion = ({ 
  question, 
  value = {}, 
  onChange, 
  interpolateText, 
  answers, 
  error 
}) => {
  const theme = useTheme();
  const [showFollowUp, setShowFollowUp] = useState(false);

  const handleOptionClick = (optionValue) => {
    const newValue = { 
      option: optionValue,
      followUp: value.followUp || ''
    };
    
    onChange(newValue);
    
    // Show follow-up if this option requires it
    const shouldShowFollowUp = 
      (question.followUpIfYes && optionValue === question.options[0]) ||
      (question.followUpIfSecond && optionValue === question.options[1]);
    
    setShowFollowUp(shouldShowFollowUp);
  };

  const handleFollowUpChange = (event) => {
    const newValue = {
      option: value.option,
      followUp: event.target.value
    };
    onChange(newValue);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, whiteSpace: 'pre-line' }}>
        {interpolateText(question.text, answers)}
      </Typography>
      
      {/* Main options */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {question.options?.map((option, index) => {
          const isSelected = value.option === option;
          
          return (
            <Card
              key={option}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                border: isSelected 
                  ? `2px solid ${theme.palette.primary.main}` 
                  : '2px solid transparent',
                bgcolor: isSelected 
                  ? `${theme.palette.primary.main}10` 
                  : 'background.paper',
                '&:hover': {
                  bgcolor: isSelected 
                    ? `${theme.palette.primary.main}20` 
                    : `${theme.palette.action.hover}`,
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[2]
                }
              }}
              onClick={() => handleOptionClick(option)}
            >
              <CardContent sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                py: 2 
              }}>
                {isSelected && (
                  <CheckCircle 
                    sx={{ 
                      color: 'primary.main', 
                      fontSize: 24, 
                      mr: 2 
                    }} 
                  />
                )}
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? 'primary.main' : 'text.primary',
                    ml: isSelected ? 0 : 4
                  }}
                >
                  {option}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Follow-up input */}
      {showFollowUp && (
        <Fade in={showFollowUp} timeout={300}>
          <Box>
            {question.followUpIfYes && value.option === question.options[0] && (
              <TextField
                fullWidth
                multiline
                rows={3}
                value={value.followUp || ''}
                onChange={handleFollowUpChange}
                placeholder={question.followUpIfYes.placeholder}
                variant="outlined"
                label="Please provide more details"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem'
                  }
                }}
              />
            )}
            
            {question.followUpIfSecond && value.option === question.options[1] && (
              <TextField
                fullWidth
                value={value.followUp || ''}
                onChange={handleFollowUpChange}
                placeholder={question.followUpIfSecond.placeholder}
                variant="outlined"
                label="Additional information"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem'
                  }
                }}
              />
            )}
          </Box>
        </Fade>
      )}
      
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ConditionalQuestion;